import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRenderEditCellParams,
  GridRowId,
  GridRowModel,
  GridRowsProp,
  useGridApiContext,
  useGridApiRef,
} from "@mui/x-data-grid";
import { Add, ArrowBack, Delete, Save } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  ComsCatalogItem,
  ComsDailyReport,
  useGetComsCatalogQuery,
  useGetComsDailyReportByIdQuery,
  useGetComsBlockByIdQuery,
  useUpdateComsDailyReportMutation,
  useRegenerateComsWeeklyReportForDateMutation,
  useRegenerateComsMonthlyReportRecordForDateMutation,
} from "../../redux/comsApi";
import { BILLS } from "./boq-constants";

// ─── Colours ──────────────────────────────────────────────────────────────────
const SECTION_COLORS = {
  activities: "#123456",
  materials: "#1565c0",
  labour: "#b71c1c",
  equipment: "#bf8c00",
} as const;

const fmt = (v: number) => `$${(v ?? 0).toFixed(2)}`;

// ─── Custom catalog-select edit cell ─────────────────────────────────────────
interface CatalogSelectCellProps extends GridRenderEditCellParams {
  catalog: ComsCatalogItem[];
  rateField: string;
  unitField: string;
  catalogIdField: string;
  groupByBill?: boolean;
}

const CatalogSelectEditCell: React.FC<CatalogSelectCellProps> = (props) => {
  const {
    id,
    field,
    catalog,
    rateField,
    unitField,
    catalogIdField,
    groupByBill,
  } = props;
  const apiRef = useGridApiContext();
  const [selectedBill, setSelectedBill] = useState<number | null>(null);

  // Bills that actually have catalog items (only relevant when groupByBill=true)
  const availableBills = useMemo(() => {
    if (!groupByBill) return [];
    const billNums = new Set(
      catalog
        .map((item) => item.billNumber)
        .filter((n): n is number => n != null),
    );
    return BILLS.filter((b) => billNums.has(b.number));
  }, [catalog, groupByBill]);

  const filteredCatalog = useMemo(
    () =>
      groupByBill && selectedBill != null
        ? catalog.filter((item) => item.billNumber === selectedBill)
        : catalog,
    [catalog, groupByBill, selectedBill],
  );

  const handleSelect = async (
    _: React.SyntheticEvent,
    item: ComsCatalogItem | null,
  ) => {
    if (item) {
      // In cell-edit mode only the active cell can be updated via setEditCellValue.
      // Use updateRows to patch the other fields directly on the committed row.
      apiRef.current.updateRows([
        {
          id,
          [unitField]: item.unit,
          [rateField]: item.defaultRate,
          [catalogIdField]: item.id,
        },
      ]);
      await apiRef.current.setEditCellValue({ id, field, value: item.name });
    }
    apiRef.current.stopCellEditMode({ id, field });
  };

  const stopEdit = (reason: string) => {
    apiRef.current.stopCellEditMode({
      id,
      field,
      ignoreModifications: reason === "escape",
    });
  };

  // ── Step 1: Bill selector (only when groupByBill and no bill chosen yet) ──
  if (groupByBill && selectedBill === null) {
    return (
      <Autocomplete
        autoHighlight
        openOnFocus
        options={availableBills}
        getOptionLabel={(opt) => `Bill ${opt.number} — ${opt.description}`}
        onChange={(_, bill) => {
          if (bill) setSelectedBill(bill.number);
        }}
        onClose={(_, reason) => {
          if (reason !== "selectOption") stopEdit(reason);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            size="small"
            fullWidth
            placeholder="Select bill…"
            sx={{
              "& .MuiInputBase-root": {
                height: "100%",
                borderRadius: 0,
                fontSize: "0.875rem",
              },
            }}
          />
        )}
        sx={{ width: "100%", height: "100%" }}
      />
    );
  }

  // ── Step 2 (or non-bill-grouped): Item selector ───────────────────────────
  return (
    <Autocomplete
      autoHighlight
      openOnFocus
      options={filteredCatalog}
      getOptionLabel={(opt) =>
        `${opt.name} (${opt.unit})${
          !groupByBill && opt.billNumber != null
            ? ` (Bill ${opt.billNumber})`
            : ""
        }`
      }
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      onChange={handleSelect}
      onClose={(_, reason) => {
        if (reason !== "selectOption") {
          // Escape while in bill-item step → go back to bill selector
          if (groupByBill && reason === "escape") {
            setSelectedBill(null);
            return;
          }
          stopEdit(reason);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus
          size="small"
          fullWidth
          placeholder={
            groupByBill && selectedBill != null
              ? `Bill ${selectedBill} — Search items…`
              : "Search catalog…"
          }
          sx={{
            "& .MuiInputBase-root": {
              height: "100%",
              borderRadius: 0,
              fontSize: "0.875rem",
            },
          }}
        />
      )}
      sx={{ width: "100%", height: "100%" }}
    />
  );
};

// ─── Section header ───────────────────────────────────────────────────────────
const SectionHeader: React.FC<{
  title: string;
  color: string;
  total: number;
}> = ({ title, color, total }) => (
  <Box
    sx={{
      bgcolor: color,
      color: "#fff",
      px: 3,
      py: 1.5,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", letterSpacing: 1 }}>
      {title}
    </Typography>
    <Chip
      label={fmt(total)}
      size="small"
      sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 700 }}
    />
  </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────
interface Props {
  blockId: string;
}

const DailyReportEditor: React.FC<Props> = ({ blockId }) => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();

  const { data: savedReport, isLoading } = useGetComsDailyReportByIdQuery(
    reportId ?? "",
    { skip: !reportId },
  );
  const [updateReport, { isLoading: saving }] =
    useUpdateComsDailyReportMutation();
  const [regenerateWeeklyForDate] = useRegenerateComsWeeklyReportForDateMutation();
  const [regenerateMonthlyForDate] = useRegenerateComsMonthlyReportRecordForDateMutation();

  // Fetch block to decide which catalogue source to use for activities
  const { data: blockData } = useGetComsBlockByIdQuery(blockId);
  const catalogueType = blockData?.projectType as "type1" | "type2" | undefined;

  const { data: activityCatalog = [] } = useGetComsCatalogQuery(
    catalogueType ? { catalogueType } : { blockId, type: "activity" },
    { skip: !blockData },
  );
  const { data: materialCatalog = [] } = useGetComsCatalogQuery({
    blockId,
    type: "material",
  });
  const { data: labourCatalog = [] } = useGetComsCatalogQuery({
    blockId,
    type: "labour",
  });
  const { data: equipmentCatalog = [] } = useGetComsCatalogQuery({
    blockId,
    type: "equipment",
  });

  // Grid API refs — used to programmatically start cell-edit on newly added rows
  const activityApiRef = useGridApiRef();
  const materialApiRef = useGridApiRef();
  const labourApiRef = useGridApiRef();
  const equipmentApiRef = useGridApiRef();

  // ── Local editable state ──
  const [activities, setActivities] = useState<GridRowsProp>([]);
  const [materials, setMaterials] = useState<GridRowsProp>([]);
  const [labour, setLabour] = useState<GridRowsProp>([]);
  const [equipment, setEquipment] = useState<GridRowsProp>([]);
  const [notes, setNotes] = useState("");
  const [dirty, setDirty] = useState(false);

  // Tracks the last reportId for which we completed catalog enrichment
  const enrichedForRef = useRef<string | null>(null);

  // Effect 1: Seed rows from the saved report (reset enrichment flag so Effect 2 re-runs)
  useEffect(() => {
    if (savedReport) {
      enrichedForRef.current = null;
      setActivities(
        (savedReport.activities ?? []).map((r: any) => ({
          ...r,
          id: r.lineId,
        })),
      );
      setMaterials(
        (savedReport.materials ?? []).map((r: any) => ({ ...r, id: r.lineId })),
      );
      setLabour(
        (savedReport.labour ?? []).map((r: any) => ({ ...r, id: r.lineId })),
      );
      setEquipment(
        (savedReport.equipment ?? []).map((r: any) => ({ ...r, id: r.lineId })),
      );
      setNotes(savedReport.notes ?? "");
      setDirty(false);
    }
  }, [savedReport]);

  // Effect 2: One-time enrichment — fill unit/rate from catalog for rows that were
  // saved before the catalog-select fix (unit is empty). Runs after both the report
  // and catalog data are available, but never marks the form dirty.
  useEffect(() => {
    if (!savedReport?.id) return;
    if (enrichedForRef.current === savedReport.id) return; // already done for this report
    const catalogsReady =
      activityCatalog.length +
      materialCatalog.length +
      labourCatalog.length +
      equipmentCatalog.length;
    if (!catalogsReady) return;

    enrichedForRef.current = savedReport.id;

    const patched = (
      rows: GridRowsProp,
      catalog: ComsCatalogItem[],
      rateField: string,
      nameKey = "name",
    ) =>
      rows.map((r) => {
        if (!r.unit && catalog.length > 0) {
          const item = r.catalogItemId
            ? catalog.find((c) => c.id === r.catalogItemId)
            : catalog.find((c) => c.name === r[nameKey]);
          if (item) {
            return {
              ...r,
              unit: item.unit,
              [rateField]: r[rateField] || item.defaultRate,
              catalogItemId: r.catalogItemId || item.id,
            };
          }
        }
        return r;
      });

    setActivities((prev) => patched(prev, activityCatalog, "rate"));
    setMaterials((prev) => patched(prev, materialCatalog, "unitPrice"));
    setLabour((prev) => patched(prev, labourCatalog, "dayRate", "grade"));
    setEquipment((prev) => patched(prev, equipmentCatalog, "hourlyRate"));
    // intentionally NO setDirty — enrichment is not a user edit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    savedReport?.id,
    activityCatalog,
    materialCatalog,
    labourCatalog,
    equipmentCatalog,
  ]);

  const mark = () => setDirty(true);

  // ── Save handler ──
  const handleSave = useCallback(async () => {
    if (!reportId) return;
    const toLine = (rows: GridRowsProp) =>
      rows.map(({ id: _, ...rest }) => ({
        ...rest,
        lineId: rest.lineId ?? uuidv4(),
      }));
    const result = await updateReport({
      id: reportId,
      data: {
        notes,
        activities: toLine(activities) as any,
        materials: toLine(materials) as any,
        labour: toLine(labour) as any,
        equipment: toLine(equipment) as any,
      },
    });
    setDirty(false);
    // D2: silently regenerate the weekly+monthly report snapshots for this date
    // so changes are immediately reflected in reports without a full backfill
    if (savedReport?.reportDate) {
      regenerateWeeklyForDate({ blockId, date: savedReport.reportDate }).catch(() => {});
      regenerateMonthlyForDate({ blockId, date: savedReport.reportDate }).catch(() => {});
    }
    return result;
  }, [reportId, notes, activities, materials, labour, equipment, updateReport, savedReport, blockId]);

  // ── Generic row add ──
  const addRow = (
    setter: React.Dispatch<React.SetStateAction<GridRowsProp>>,
    defaults: Record<string, unknown>,
    gridApiRef: ReturnType<typeof useGridApiRef>,
    editField: string,
  ) => {
    const lineId = uuidv4();
    setter((prev) => [...prev, { id: lineId, lineId, ...defaults }]);
    mark();
    // Open the catalog dropdown on the new row after it renders
    setTimeout(() => {
      try {
        gridApiRef.current.startCellEditMode({ id: lineId, field: editField });
      } catch (_) {}
    }, 50);
  };

  // ── Generic row delete ──
  const deleteRow = (
    setter: React.Dispatch<React.SetStateAction<GridRowsProp>>,
    rowId: GridRowId,
  ) => {
    setter((prev) => prev.filter((r) => r.id !== rowId));
    mark();
  };

  // ── Generic processRowUpdate ──
  const makeUpdater =
    (
      setter: React.Dispatch<React.SetStateAction<GridRowsProp>>,
      amountFn: (row: GridRowModel) => number,
    ) =>
    (newRow: GridRowModel) => {
      const amount = amountFn(newRow);
      const updated = { ...newRow, amount } as unknown as GridRowModel & {
        id: GridRowId;
      };
      setter((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      mark();
      return updated;
    };

  // ── Day totals ──
  const sumAmount = (rows: GridRowsProp) =>
    rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  const totalRevenue = sumAmount(activities);
  const totalMaterials = sumAmount(materials);
  const totalLabour = sumAmount(labour);
  const totalEquipment = sumAmount(equipment);
  const totalProfit =
    totalRevenue - totalMaterials - totalLabour - totalEquipment;

  // ─────────────────────────────────────────────────────────────────────────────
  // Column definitions
  // ─────────────────────────────────────────────────────────────────────────────

  const actionsCol = (
    setter: React.Dispatch<React.SetStateAction<GridRowsProp>>,
  ): GridColDef => ({
    field: "actions",
    type: "actions",
    width: 60,
    getActions: (params) => [
      <GridActionsCellItem
        key="del"
        icon={<Delete fontSize="small" color="error" />}
        label="Delete"
        onClick={() => deleteRow(setter, params.id)}
      />,
    ],
  });

  const makeNameEditCell =
    (
      catalog: ComsCatalogItem[],
      rateField: string,
      unitField: string,
      catalogIdField: string,
      groupByBill?: boolean,
    ) =>
    (params: GridRenderEditCellParams) =>
      (
        <CatalogSelectEditCell
          {...params}
          catalog={catalog}
          rateField={rateField}
          unitField={unitField}
          catalogIdField={catalogIdField}
          groupByBill={groupByBill}
        />
      );

  // ── Bill-grouped display rows for activities ─────────────────────────────
  // Build catalogItemId / name → billNumber lookup
  const catalogBillMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const item of activityCatalog) {
      if (item.billNumber != null) {
        if (item.id) m.set(`id:${item.id}`, item.billNumber);
        m.set(`name:${item.name}`, item.billNumber);
      }
    }
    return m;
  }, [activityCatalog]);

  // Enrich saved rows with billNumber from the catalog
  const enrichedActivities = useMemo(
    () =>
      activities.map((row) => ({
        ...row,
        billNumber:
          row.billNumber ??
          (row.catalogItemId
            ? catalogBillMap.get(`id:${row.catalogItemId}`)
            : undefined) ??
          catalogBillMap.get(`name:${String(row.name ?? "")}`) ??
          undefined,
      })),
    [activities, catalogBillMap],
  );

  // Sort by bill and insert a header row before each new bill group
  const groupedActivities = useMemo(() => {
    const sorted = [...enrichedActivities].sort(
      (a, b) => (Number(a.billNumber) || 999) - (Number(b.billNumber) || 999),
    );
    const groups: {
      bill: number | null;
      billDesc: string;
      rows: GridRowsProp;
    }[] = [];
    let current: (typeof groups)[0] | null = null;
    for (const row of sorted) {
      const bill = row.billNumber != null ? Number(row.billNumber) : null;
      if (!current || current.bill !== bill) {
        const entry = bill ? BILLS.find((b) => b.number === bill) : null;
        current = {
          bill,
          billDesc:
            entry?.description ?? (bill ? `Bill ${bill}` : "Unassigned"),
          rows: [],
        };
        groups.push(current);
      }
      current.rows.push(row);
    }
    return groups;
  }, [enrichedActivities]);

  // Activities columns
  const activityCols: GridColDef[] = [
    {
      field: "name",
      headerName: "ACTIVITY",
      flex: 1,
      editable: true,
      renderEditCell: makeNameEditCell(
        activityCatalog,
        "rate",
        "unit",
        "catalogItemId",
        true,
      ),
      renderCell: (params) => (
        <Box
          sx={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.5,
            py: 0.5,
          }}
        >
          {params.value}
          {params.row.billNumber != null
            ? ` (Bill ${params.row.billNumber})`
            : ""}
        </Box>
      ),
    },
    { field: "unit", headerName: "UNIT", width: 90, editable: false },
    {
      field: "quantity",
      headerName: "QUANTITY",
      width: 110,
      type: "number",
      editable: true,
    },
    {
      field: "rate",
      headerName: "RATE",
      width: 110,
      type: "number",
      editable: true,
      valueFormatter: (v: any) => `$${(v ?? 0).toFixed(2)}`,
    },
    {
      field: "amount",
      headerName: "AMOUNT",
      width: 120,
      type: "number",
      editable: false,
      valueFormatter: (v: any) => fmt(v),
    },
    actionsCol(setActivities),
  ];

  // Materials columns
  const materialCols: GridColDef[] = [
    {
      field: "name",
      headerName: "MATERIAL",
      flex: 1,
      editable: true,
      renderEditCell: makeNameEditCell(
        materialCatalog,
        "unitPrice",
        "unit",
        "catalogItemId",
      ),
    },
    { field: "unit", headerName: "UNIT", width: 90, editable: false },
    {
      field: "quantity",
      headerName: "QUANTITY",
      width: 110,
      type: "number",
      editable: true,
    },
    {
      field: "unitPrice",
      headerName: "UNIT PRICE",
      width: 110,
      type: "number",
      editable: true,
      valueFormatter: (v: any) => `$${(v ?? 0).toFixed(2)}`,
    },
    {
      field: "amount",
      headerName: "AMOUNT",
      width: 120,
      type: "number",
      editable: false,
      valueFormatter: (v: any) => fmt(v),
    },
    actionsCol(setMaterials),
  ];

  // Labour columns
  const labourCols: GridColDef[] = [
    {
      field: "grade",
      headerName: "GRADE",
      flex: 1,
      editable: true,
      renderEditCell: makeNameEditCell(
        labourCatalog,
        "dayRate",
        "unit",
        "catalogItemId",
      ),
    },
    { field: "unit", headerName: "UNIT", width: 90, editable: false },
    {
      field: "people",
      headerName: "PEOPLE",
      width: 100,
      type: "number",
      editable: true,
    },
    {
      field: "dayRate",
      headerName: "DAY RATE",
      width: 110,
      type: "number",
      editable: true,
      valueFormatter: (v: any) => `$${(v ?? 0).toFixed(2)}`,
    },
    {
      field: "amount",
      headerName: "AMOUNT",
      width: 120,
      type: "number",
      editable: false,
      valueFormatter: (v: any) => fmt(v),
    },
    actionsCol(setLabour),
  ];

  // Equipment columns
  const equipmentCols: GridColDef[] = [
    {
      field: "name",
      headerName: "EQUIPMENT",
      flex: 1,
      editable: true,
      renderEditCell: makeNameEditCell(
        equipmentCatalog,
        "hourlyRate",
        "unit",
        "catalogItemId",
      ),
    },
    { field: "unit", headerName: "UNIT", width: 90, editable: false },
    {
      field: "hours",
      headerName: "HOURS",
      width: 100,
      type: "number",
      editable: true,
    },
    {
      field: "hourlyRate",
      headerName: "HOURLY RATE",
      width: 120,
      type: "number",
      editable: true,
      valueFormatter: (v: any) => `$${(v ?? 0).toFixed(2)}`,
    },
    {
      field: "amount",
      headerName: "AMOUNT",
      width: 120,
      type: "number",
      editable: false,
      valueFormatter: (v: any) => fmt(v),
    },
    actionsCol(setEquipment),
  ];

  // ─────────────────────────────────────────────────────────────────────────────

  if (isLoading)
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  if (!savedReport)
    return <Typography color="error">Report not found.</Typography>;

  return (
    <Box>
      {/* ── Top bar ── */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{ color: "#123456" }}
          >
            Back to reports
          </Button>
          <Typography variant="body2" color="text.secondary">
            Report for <strong>{savedReport.reportDate}</strong>
          </Typography>
        </Box>
        <Tooltip title={dirty ? "Save all changes" : "No unsaved changes"}>
          <span>
            <Button
              variant="contained"
              startIcon={
                saving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Save />
                )
              }
              onClick={handleSave}
              disabled={!dirty || saving}
              sx={{ bgcolor: "#123456", "&:hover": { bgcolor: "#1a4a7a" } }}
            >
              {saving ? "Saving…" : "Save Report"}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Paper sx={{ p: 3, mb: 2, borderRadius: 2, bgcolor: "#fafafa" }}>
        <Typography variant="subtitle1">
          Daily Report — Excel-style editor
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Double-click any cell to edit it. Use the catalog dropdown to
          auto-fill rates. Click{" "}
          <strong>+ Add Activity / Material / Labour / Equipment</strong> to
          insert a new line. Click <strong>Save Report</strong> when done.
        </Typography>
      </Paper>

      {/* ══ ACTIVITIES (REVENUE) ══════════════════════════════════════════════ */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
        <SectionHeader
          title="CALCULATION OF DAY'S WORK (REVENUE)"
          color={SECTION_COLORS.activities}
          total={totalRevenue}
        />

        {groupedActivities.map(({ bill, billDesc, rows }) => (
          <Box key={bill ?? "unassigned"}>
            {/* Bill group header */}
            <Box
              sx={{
                px: 3,
                py: 1,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                bgcolor: "#f0f4ff",
                borderLeft: "4px solid #123456",
                borderBottom: "1px solid #dce6ff",
              }}
            >
              <Chip
                label={bill != null ? `Bill ${bill}` : "Unassigned"}
                size="small"
                sx={{
                  bgcolor: bill != null ? "#123456" : "#757575",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  height: 22,
                }}
              />
              <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                {billDesc}
              </Typography>
              <Chip
                label={rows.length}
                size="small"
                sx={{ bgcolor: "#e0e7ff", fontWeight: 600 }}
              />
            </Box>

            <DataGrid
              autoHeight
              rows={rows}
              columns={activityCols}
              editMode="cell"
              hideFooter
              disableRowSelectionOnClick
              getRowHeight={() => "auto"}
              processRowUpdate={makeUpdater(
                setActivities,
                (r) => (Number(r.quantity) || 0) * (Number(r.rate) || 0),
              )}
              onProcessRowUpdateError={(e) => console.error(e)}
              sx={{
                ...gridSx,
                "& .MuiDataGrid-cell": { alignItems: "flex-start" },
              }}
            />
          </Box>
        ))}

        {groupedActivities.length === 0 && (
          <Typography sx={{ p: 2, color: "text.secondary" }}>
            No activities yet. Click "+ Add Activity" to get started.
          </Typography>
        )}

        <Box px={2} py={1}>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() =>
              addRow(
                setActivities,
                {
                  catalogItemId: "",
                  name: "",
                  unit: "",
                  quantity: 0,
                  rate: 0,
                  amount: 0,
                },
                activityApiRef,
                "name",
              )
            }
            sx={{ color: SECTION_COLORS.activities }}
          >
            Add Activity
          </Button>
        </Box>
      </Paper>

      {/* ══ MATERIALS ════════════════════════════════════════════════════════ */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
        <SectionHeader
          title="MATERIALS USED (COST)"
          color={SECTION_COLORS.materials}
          total={totalMaterials}
        />
        <DataGrid
          apiRef={materialApiRef}
          autoHeight
          rows={materials}
          columns={materialCols}
          editMode="cell"
          hideFooter
          disableRowSelectionOnClick
          processRowUpdate={makeUpdater(
            setMaterials,
            (r) => (Number(r.quantity) || 0) * (Number(r.unitPrice) || 0),
          )}
          onProcessRowUpdateError={(e) => console.error(e)}
          sx={gridSx}
        />
        <Box px={2} py={1}>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() =>
              addRow(
                setMaterials,
                {
                  catalogItemId: "",
                  name: "",
                  unit: "",
                  quantity: 0,
                  unitPrice: 0,
                  amount: 0,
                },
                materialApiRef,
                "name",
              )
            }
            sx={{ color: SECTION_COLORS.materials }}
          >
            Add Material
          </Button>
        </Box>
      </Paper>

      {/* ══ LABOUR ═══════════════════════════════════════════════════════════ */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
        <SectionHeader
          title="LABOUR (COST)"
          color={SECTION_COLORS.labour}
          total={totalLabour}
        />
        <DataGrid
          apiRef={labourApiRef}
          autoHeight
          rows={labour}
          columns={labourCols}
          editMode="cell"
          hideFooter
          disableRowSelectionOnClick
          processRowUpdate={makeUpdater(
            setLabour,
            (r) => (Number(r.people) || 0) * (Number(r.dayRate) || 0),
          )}
          onProcessRowUpdateError={(e) => console.error(e)}
          sx={gridSx}
        />
        <Box px={2} py={1}>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() =>
              addRow(
                setLabour,
                {
                  catalogItemId: "",
                  grade: "",
                  unit: "",
                  people: 0,
                  dayRate: 0,
                  amount: 0,
                },
                labourApiRef,
                "grade",
              )
            }
            sx={{ color: SECTION_COLORS.labour }}
          >
            Add Labour
          </Button>
        </Box>
      </Paper>

      {/* ══ EQUIPMENT / PLANT ════════════════════════════════════════════════ */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
        <SectionHeader
          title="EQUIPMENT / PLANT (COST)"
          color={SECTION_COLORS.equipment}
          total={totalEquipment}
        />
        <DataGrid
          apiRef={equipmentApiRef}
          autoHeight
          rows={equipment}
          columns={equipmentCols}
          editMode="cell"
          hideFooter
          disableRowSelectionOnClick
          processRowUpdate={makeUpdater(
            setEquipment,
            (r) => (Number(r.hours) || 0) * (Number(r.hourlyRate) || 0),
          )}
          onProcessRowUpdateError={(e) => console.error(e)}
          sx={gridSx}
        />
        <Box px={2} py={1}>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() =>
              addRow(
                setEquipment,
                {
                  catalogItemId: "",
                  name: "",
                  unit: "",
                  hours: 0,
                  hourlyRate: 0,
                  amount: 0,
                },
                equipmentApiRef,
                "name",
              )
            }
            sx={{ color: SECTION_COLORS.equipment }}
          >
            Add Equipment
          </Button>
        </Box>
      </Paper>

      {/* ══ DAY TOTALS ═══════════════════════════════════════════════════════ */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}>
        <Box
          sx={{
            bgcolor: totalProfit < 0 ? "#c62828" : "#f5f5f5",
            px: 3,
            py: 1.5,
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.85rem",
              letterSpacing: 1,
              color: totalProfit < 0 ? "#fff" : "#555",
            }}
          >
            DAY TOTALS
          </Typography>
          {totalProfit < 0 && (
            <Typography
              sx={{ color: "#fff", fontSize: "0.8rem", fontWeight: 600 }}
            >
              ⚠ LOSS DAY — Review costs and plan corrective action
            </Typography>
          )}
        </Box>
        <Box display="flex" flexWrap="wrap" gap={0}>
          {[
            {
              label: "Revenue",
              value: totalRevenue,
              color: SECTION_COLORS.activities,
            },
            {
              label: "Materials",
              value: -totalMaterials,
              color: SECTION_COLORS.materials,
            },
            {
              label: "Labour",
              value: -totalLabour,
              color: SECTION_COLORS.labour,
            },
            {
              label: "Equipment",
              value: -totalEquipment,
              color: SECTION_COLORS.equipment,
            },
            {
              label: totalProfit < 0 ? "LOSS" : "PROFIT",
              value: totalProfit,
              color: totalProfit < 0 ? "#c62828" : "#123456",
            },
          ].map(({ label, value, color }) => (
            <Box
              key={label}
              sx={{ flex: "1 1 120px", px: 3, py: 2, textAlign: "center" }}
            >
              <Typography variant="body2" color="text.secondary">
                {label}
              </Typography>
              <Typography
                sx={{
                  fontWeight:
                    label === "PROFIT" || label === "LOSS" ? 900 : 600,
                  color: value < 0 ? "#c62828" : color,
                  fontSize:
                    label === "PROFIT" || label === "LOSS" ? "1.2rem" : "1rem",
                }}
              >
                {label === "PROFIT" || label === "LOSS"
                  ? fmt(value)
                  : fmt(Math.abs(value))}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* ══ NOTES ════════════════════════════════════════════════════════════ */}
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Notes / Comments
        </Typography>
        <TextField
          multiline
          rows={4}
          fullWidth
          placeholder="General notes about the day (weather, visits, issues…)"
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            mark();
          }}
          variant="outlined"
        />
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={handleSave}
            disabled={!dirty || saving}
            sx={{ borderColor: "#123456", color: "#123456" }}
          >
            Save notes
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

// Shared DataGrid sx override to match VMS theme
const gridSx = {
  border: 0,
  "& .MuiDataGrid-columnHeaders": { bgcolor: "#f9f9f9", fontWeight: 700 },
  "& .MuiDataGrid-cell:focus": { outline: "none" },
  "& .MuiDataGrid-row:hover": { bgcolor: "rgba(18,52,86,0.04)" },
  "& .MuiDataGrid-cell--editing": { bgcolor: "rgba(18,52,86,0.06) !important" },
};

export default DailyReportEditor;
